import {
  getAwardLadder,
  resolveAwards,
  scopeLabel,
  type AwardTier,
  type SeasonScope,
  type SeasonStanding,
} from '@shared/seasons';
import { awardMedallion, awardTierKey } from './seasonAwardVisuals';

/** One placement award the player currently holds, with the scope that earned it. */
interface ScopeAward {
  scope: SeasonScope;
  tier: AwardTier;
}

/**
 * Your season trophy case: the single best placement award you currently hold
 * in each scope (Overall + each mode), gathered into one global rail. You earn
 * at most one award per scope — your most prestigious satisfied tier — so a
 * 7th-place finish shows just "Top 10", not every cut-off beneath it. Each
 * badge is tagged with its season (`S1`) and scope so the case stays legible
 * once seasons start to roll.
 *
 * Sourced from cross-scope `/api/seasons/me` standings (via `useSeason`), this
 * sits above the scope picker and is independent of it — switching tabs never
 * reloads or hides it.
 */
export function SeasonAwardsGallery({
  standings,
  seasonNumber,
}: {
  standings: SeasonStanding[];
  seasonNumber: number | null;
}) {
  // Standings only carry a real season once `/me` has resolved; until then
  // there's nothing honest to show, so render nothing rather than flash an
  // empty state that would immediately be replaced.
  if (seasonNumber === null) {
    return null;
  }

  const seasonTag = `S${seasonNumber}`;
  const awards: ScopeAward[] = standings.flatMap((s) => {
    const { current } = resolveAwards(s.rank, getAwardLadder(s.participants));
    return current ? [{ scope: s.scope, tier: current }] : [];
  });

  return (
    <section className="season-awards">
      <h2 className="season-section-title">Your awards</h2>
      {awards.length === 0 ? (
        <p className="season-awards-empty">
          No awards yet — finish near the top of a leaderboard to earn your first.
        </p>
      ) : (
        <ul className="season-awards-rail">
          {awards.map(({ scope, tier }) => (
            <li
              key={scope}
              className={`season-award season-award-${awardTierKey(tier.threshold)} is-won`}
              title={`${seasonTag} · ${scopeLabel(scope)} — ${tier.label}`}
            >
              <span className="season-award-season">{seasonTag}</span>
              <span className="season-award-medallion" aria-hidden="true">
                {awardMedallion(tier)}
              </span>
              <span className="season-award-label">{tier.label}</span>
              <span className="season-award-scope">{scopeLabel(scope)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
